// The step drawer: the fields of every node kind, broker tools and the pre-execution script.
import type { ReactNode } from "react";

import type { NodeKind } from "../../../../catalog/types";
import { rich } from "../../../helpers";

type Mark = (text: string) => ReactNode;

export default {
  kindStep: { prompt: "Prompt step", approval: "Approval step", orchestrate: "Subtasks step", end: "End step" } satisfies Record<NodeKind, string>,
  deleteStep: "Delete step",
  startStep: "Start step",
  setStart: "Set as start",
  name: "Name",
  result: "Result",
  issues: "Issues",
  id: "Id",
  idInvalid: "1–64 letters, digits, '-' or '_'.",
  idTaken: "Another node has this id.",
  approval: {
    question: "Question",
    questionPlaceholder: "Approve the plan?",
    options: "Options",
    optionsPlaceholder: "approve, revise",
  },
  maxVisits: "Max visits",
  prompt: {
    prompt: "Prompt",
    placeholder: "What the agent should do in this step",
    insertVariable: "Insert a variable",
    footerHint: "A footer is appended: work in /workspace, summarise at the end, report declared outputs.",
    agentCli: "Agent CLI",
    cursorSoon: "Cursor (coming soon)",
    cursorRefused: "Saved, but a Cursor step is refused when it would run until Cursor is supported.",
    model: "Model",
    platformDefault: "Platform default",
    modelLatest: (name: string) => `${name} (latest)`,
    modelCustom: (id: string) => `${id} (custom)`,
    timeoutMinutes: "Timeout (minutes)",
    needsDocker: "Needs Docker",
    outputs: "Outputs",
    outputsHint: "Keys the agent may report (report verdict=pass) for edges to branch on.",
  },
  tools: {
    title: "Broker tools",
    none: "None: the step can only work in its workspace.",
    row: (n: number) => `Tool ${n}`,
    tool: "Tool",
    remove: (n: number) => `Remove tool ${n}`,
    actions: "Actions",
    needsApproval: "Each call needs the owner's approval",
    add: "Add tool",
  },
  script: {
    section: "Pre-execution script",
    hint: "A deterministic check before the agent: skip steps that have nothing to do.",
    toggle: "Run a pre-execution script",
    language: "Language",
    timeoutSeconds: "Timeout (seconds)",
    script: "Script",
    limits: (max: number) => `At most 64 KB; times out after 1–${max} seconds (30 by default).`,
    helpTitle: "How the script decides",
    helpRuns: (m: Mark) =>
      rich(
        "The worker runs it as ", m("agent"), " in ", m("/workspace"), " before the agent, with the task context as JSON on stdin: ",
        m("task"), ", ", m("group"), ", ", m("node"), ", ", m("visit"), " and the earlier steps' ", m("outputs"), ".",
      ),
    helpExit0: (b: Mark) => rich("Exit ", b("0"), ": the step runs."),
    helpExit3: (b: Mark, m: Mark) =>
      rich("Exit ", b("3"), ": the step is skipped without spending tokens; its outcome is ", m("skipped"), " (branch on it) and the last line of stdout, if it is a JSON object, becomes the step's outputs."),
    helpOther: "Any other exit, a timeout or a crash: the step runs anyway and the error is recorded.",
    broker: {
      title: "Broker calls",
      hint: "Read-only calls the script may make with its own token, which is revoked before the agent starts; the agent never holds it.",
      nameFirst: "Name a credential requirement on the Requirements tab (for example jira or github) to grant a call with its secret.",
      loadFailed: "The list of broker calls could not be loaded; reload the page to add one.",
      row: (n: number) => `Broker call ${n}`,
      action: "Action",
      requirement: "Requirement",
      choose: "Choose a requirement",
      remove: (n: number) => `Remove broker call ${n}`,
      add: "Add a broker call",
      usage: (m: Mark) => rich("In Python: ", m("from ocs import broker"), ", then ", m("broker.call(\"jira\", \"issues.search\", requirement=\"jira\", jql=\"…\")"), "; in Bash: ", m("ocs-tool call jira issues.search --requirement jira jql=…"), "."),
      actions: {
        "jira.get": "Jira: the task's own issue",
        "jira.issues.search": "Jira: search with JQL",
        "github.pulls.list": "GitHub: list pull requests",
        "github.pulls.reviews": "GitHub: a pull request's reviews",
        "github.pulls.comments": "GitHub: a pull request's review comments",
        "github.issues.comments": "GitHub: a pull request's conversation comments",
      },
    },
  },
};
